
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

// Define types for page data
export interface PageData {
  id: string;
  path: string;
  data?: any;
  timestamp: number;
}

/**
 * Hook to fetch page data with React Query
 */
export const usePageData = (path: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Function to fetch data for a specific page path
  const fetchPageData = async (path: string): Promise<PageData> => {
    // Here you would typically fetch data from an API
    // For now, simulate a fetch with some delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // In a real implementation, this would be data from your API
    return {
      id: path,
      path,
      data: { title: `Page data for ${path}` },
      timestamp: Date.now()
    };
  };
  
  // Set up the query
  const query = useQuery({
    queryKey: ['page', path],
    queryFn: () => fetchPageData(path),
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Don't refetch on window focus for better UX during navigation
    refetchOnWindowFocus: false
  });
  
  // Handle errors
  useEffect(() => {
    if (query.error) {
      console.error("Error loading page data:", query.error);
      toast({
        title: "Error Loading Content",
        description: "There was a problem loading the page content. Please try again.",
        variant: "destructive",
      });
    }
  }, [query.error, toast]);
  
  // Prefetch method
  const prefetch = (prefetchPath: string) => {
    queryClient.prefetchQuery({
      queryKey: ['page', prefetchPath],
      queryFn: () => fetchPageData(prefetchPath),
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  };
  
  return {
    ...query,
    prefetch
  };
};
